import { datadogRum } from '@datadog/browser-rum';
import {
  AccessToken,
  AuthApiError,
  AuthSdkError,
  OAuthError,
  Token,
} from '@okta/okta-auth-js';
import oktaAuth, { clearAuthSubscribers } from 'okta/oktaConfig';
import z from 'zod';

import { handleApiError } from '@/utils/api-error-handler';
import { setWindowObjectToConnectAppToCypress } from '@/utils/environment';

import { NativeAndroidUser, OktaTokenStorage, OktaUserRaw } from '@/types';

import Constants from 'constants/index';

import { setImpersonatedToken } from './permissions-widget/token';

import { resetAllStorageAtoms } from '@/atoms/resetStorage';

export const setReturnTo = () => {
  if (!['/login', '/login/callback'].includes(window.location.pathname)) {
    const fullPath =
      window.location.pathname + window.location.search + window.location.hash;
    localStorage.setItem('returnTo', fullPath);
  }
};

const clearOktaLocalStorage = () => {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('okta')) localStorage.removeItem(k);
    }
  } catch (e) {
    if (e instanceof Error) {
      console.warn(`clearOktaLocalStorage failed ${e.message}`);
    } else {
      console.warn(`clearOktaLocalStorage failed ${e}`);
    }
  }
};
export const logout = async () => {
  setImpersonatedToken(undefined);
  // Clear all jotai atoms with storage.
  resetAllStorageAtoms();
  clearAuthSubscribers();

  try {
    // Preferred path: Will always work (even when user lacks idToken) when this origin is
    // in Okta Trusted Origins (CORS)
    await oktaAuth.signOut({
      postLogoutRedirectUri: Constants.PATHWAY_LOGOUT_REDIRECT,
    });
    return;
  } catch (e) {
    console.warn(
      'oktaAuth.signOut failed; falling back to redirect-based logout',
      e,
    );
    datadogRum.addError(
      new Error(
        `signOut failed; using fallback redirect: ${e instanceof Error ? e.message : e}`,
      ),
    );
  }

  // Fallback path: no CORS dependency, forces fresh auth
  await oktaAuth.signInWithRedirect({
    originalUri: Constants.PATHWAY_LOGOUT_REDIRECT ?? '/',
    prompt: 'login',
    maxAge: 0,
  });
};

export const clearOktaAndLogout = async () => {
  console.warn('Clearing Okta session and logging out...');
  setReturnTo();
  clearOktaLocalStorage();
  return logout();
};

export const signInWithRedirect: typeof oktaAuth.signInWithRedirect =
  async opts => {
    if (Constants.IS_IN_CYPRESS_TEST_LOCAL) {
      setWindowObjectToConnectAppToCypress('signInWithRedirect', 'true');
      return;
    }
    try {
      setReturnTo();
      await oktaAuth.signInWithRedirect(opts);
    } catch (err: unknown) {
      let status: number | undefined;
      let errorCode: string | undefined;
      let errorSummary: string | undefined;

      if (err instanceof AuthApiError || err instanceof AuthSdkError) {
        status = err.xhr?.status;
        errorCode = err.errorCode;
        errorSummary = err.errorSummary;
      } else if (err instanceof OAuthError) {
        errorCode = err.errorCode;
        errorSummary = err.errorSummary;
      }

      datadogRum.addError(err, {
        section: 'okta',
        status,
        errorCode,
        errorSummary,
      });
      await clearOktaAndLogout();
    }
  };

type TryRefreshResult =
  | { success: true; token: string }
  | { success: false; error: unknown };

/**
 * Attempt silent token refresh without auto-redirecting on failure.
 * Use this when you want to handle failure yourself (e.g., show a warning first).
 */
export const tryRefreshSilently = async (): Promise<TryRefreshResult> => {
  // Debug hook: allow forcing failure for testing (set via window.debugAuth.failNextRefresh())
  const w = window as { _debugFailNextRefresh?: boolean };
  if (w._debugFailNextRefresh) {
    w._debugFailNextRefresh = false;
    return { success: false, error: new Error('Forced failure for testing') };
  }

  try {
    // Uses the refresh token (via XHR POST /token) when present, which is
    // resilient to hard refreshes and not subject to Safari ITP / 3rd-party
    // cookie restrictions. Falls back to the iframe-based getWithoutPrompt
    // only if no refresh token exists (legacy sessions minted before the
    // offline_access scope was added).
    const renewed = (await oktaAuth.tokenManager.renew('accessToken')) as
      | AccessToken
      | undefined;

    const token = renewed?.accessToken;
    if (!token) {
      return {
        success: false,
        error: new Error('No access token after renew'),
      };
    }

    return { success: true, token };
  } catch (e) {
    return { success: false, error: e };
  }
};

/**
 * Silently refresh tokens using Okta's SSO session (iframe-based).
 * Falls back to full redirect login if SSO session has expired.
 *
 * @returns The new access token string, or undefined if redirecting
 */
export const refreshTokensSilently = async (): Promise<string | undefined> => {
  if (Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST) {
    return;
  }

  const result = await tryRefreshSilently();
  if (result.success) {
    return result.token;
  }

  setReturnTo();
  await signInWithRedirect();
  return undefined;
};

export const isOktaTokenStorage = (
  value: unknown,
): value is OktaTokenStorage => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.accessToken && typeof v.accessToken === 'object') {
    const accessToken = v.accessToken as Record<string, unknown>;
    return typeof accessToken.accessToken === 'string';
  }
  return false;
};

/** Error has `status` `401`. */
export const isUnAuthorizedError = (error: unknown): boolean =>
  z.object({ status: z.number() }).safeParse(error).data?.status === 401;

interface NativeAuthNative {
  /** Whether or not the app is currently running in a native WebView. */
  isNative: true;
  /** The access token of the user from native. */
  accessToken: string;
  /** The auth user from native. */
  nativeUser: OktaUserRaw;
}

interface NativeAuthNonNative {
  isNative: false;
  accessToken: undefined;
  nativeUser: undefined;
}

export type NativeAuth = NativeAuthNative | NativeAuthNonNative;

/** Get auth from the native app. */
export const getAuthFromNative = (): NativeAuth => {
  const androidUser = getAndroidUser();

  const user = window.userFromToken
    ? (JSON.parse(window.userFromToken) as OktaUserRaw)
    : androidUser?.userFromToken;

  const token =
    (window as { accessToken?: string }).accessToken ??
    androidUser?.accessToken;

  const isNative = user !== undefined && token !== undefined;

  if (isNative) {
    return {
      isNative: true,
      accessToken: token,
      nativeUser: user,
    };
  }

  return {
    isNative: false,
    accessToken: undefined,
    nativeUser: undefined,
  };
};

export const getAndroidUser = (): NativeAndroidUser | undefined => {
  const injectedObjectJson = window.ReactNativeWebView?.injectedObjectJson;
  if (!injectedObjectJson) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(injectedObjectJson());
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as NativeAndroidUser)
      : undefined;
  } catch (error) {
    console.error('Native Error parsing Android user:', error);
    return undefined;
  }
};

export const getApiToken = async (): Promise<string> => {
  try {
    if (Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST) {
      // parse stored okta token; use shared OktaTokenStorage type
      const rawStorage = JSON.parse(
        localStorage.getItem('okta-token-storage') ?? 'null',
      ) as unknown;
      const storage = isOktaTokenStorage(rawStorage) ? rawStorage : undefined;

      const token = storage?.accessToken?.accessToken;
      if (!token) {
        throw new Error('No access token found in storage');
      }
      return token;
    }

    const nativeAuth = getAuthFromNative();
    if (nativeAuth.isNative) {
      return nativeAuth.accessToken;
    }

    const webToken = oktaAuth.getAccessToken();

    const tokens = oktaAuth.tokenManager.getTokensSync();
    if (
      (tokens.accessToken && getTokenExpiresInOneMinute(tokens.accessToken)) ||
      !webToken
    ) {
      const newToken = await refreshTokensSilently();
      if (newToken) {
        return newToken;
      }
      throw new Error('Redirecting to reauthenticate');
    }

    return webToken;
  } catch (e: unknown) {
    console.log('[getApiToken] Error:', e);

    // Delegate to centralized error handler
    // Handles: skippable errors, Okta auth errors, 403 during impersonation,
    // 401 token errors (toast during impersonation, logout otherwise)
    await handleApiError(e, 'getApiToken');

    // If handleApiError didn't throw, the error wasn't auth-related
    // Just rethrow for the calling code to handle
    throw e;
  }
};

const getTokenExpiresInOneMinute = (token: Token): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  const expiresAt = token.expiresAt;
  return expiresAt - currentTime <= 60;
};
